import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { getLeaderboard, getScanLeaderboard } from '../lib/api';
import { Trophy, Fire, Lightning, Star, Medal, HandGrabbing, Timer, Target, Warning, Barcode, Package, Factory, Truck } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const medalColors = {
  gold: 'bg-yellow-400 text-yellow-900 border-yellow-500',
  silver: 'bg-slate-300 text-slate-800 border-slate-400',
  bronze: 'bg-amber-700 text-amber-50 border-amber-800',
};

const badgeIcons = {
  century: Trophy,
  five_hundred: Trophy,
  streak3: Fire,
  speed: Lightning,
  hot_week: Star,
};

function BadgeChip({ badge }) {
  const Icon = badgeIcons[badge.code] || Star;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 bg-brand text-white text-[10px] font-bold uppercase tracking-widest"
      title={badge.label}
    >
      <Icon size={10} weight="fill" /> {badge.label}
    </span>
  );
}

export default function Leaderboard() {
  const [data, setData] = useState({ entries: [] });
  const [scanData, setScanData] = useState({ entries: [] });
  const [loading, setLoading] = useState(true);
  const [scanLoading, setScanLoading] = useState(true);
  const [period, setPeriod] = useState(7);
  const [tab, setTab] = useState('pickers');
  const [accuracy, setAccuracy] = useState({ rows: [], total_scans: 0, total_mis_scans: 0, accuracy_pct: 100 });
  const [accuracyLoading, setAccuracyLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await getLeaderboard({ lookback_days: period });
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchAccuracy = async () => {
    setAccuracyLoading(true);
    try {
      const r = await axios.get(`${API}/api/picking/scan-accuracy`, {
        params: { days: period },
        withCredentials: true,
      });
      setAccuracy(r.data || { rows: [] });
    } catch (e) {
      console.error(e);
    } finally {
      setAccuracyLoading(false);
    }
  };

  const fetchScanLeaderboard = async () => {
    setScanLoading(true);
    try {
      const res = await getScanLeaderboard({ lookback_days: period });
      setScanData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setScanLoading(false);
    }
  };

  useEffect(() => { fetchData(); fetchAccuracy(); fetchScanLeaderboard(); }, [period]);

  const top3 = (data.entries || []).slice(0, 3);
  const rest = (data.entries || []).slice(3);

  return (
    <Layout>
      <div data-testid="leaderboard-page" className="p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
              <Trophy size={32} weight="fill" className="text-yellow-500" />
              Picker Leaderboard
            </h1>
            <p className="text-muted-foreground">Compete, streak, and level up your warehouse game</p>
          </div>
          <div className="flex items-center gap-2">
            <label className="overline">Period</label>
            <select
              value={period}
              onChange={(e) => setPeriod(parseInt(e.target.value))}
              className="px-3 py-2 border border-border font-medium"
              data-testid="leaderboard-period-select"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>
        </div>

        {/* Leaderboard tab switcher — Pickers (existing) or Scan-speed (new) */}
        <div className="grid grid-cols-2 gap-1 mb-6 p-1 bg-muted rounded-md max-w-md" data-testid="leaderboard-tabs">
          <button
            type="button"
            onClick={() => setTab('pickers')}
            data-testid="tab-pickers"
            className={`py-2 text-xs font-bold uppercase tracking-wider rounded transition flex items-center justify-center gap-1.5 ${
              tab === 'pickers' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Trophy size={14} weight="fill" /> Pickers
          </button>
          <button
            type="button"
            onClick={() => setTab('scan_speed')}
            data-testid="tab-scan-speed"
            className={`py-2 text-xs font-bold uppercase tracking-wider rounded transition flex items-center justify-center gap-1.5 ${
              tab === 'scan_speed' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Barcode size={14} weight="fill" /> Scan speed
          </button>
        </div>

        {tab === 'scan_speed' ? (
          <ScanSpeedBoard data={scanData} loading={scanLoading} />
        ) : (
        <>
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            {[1,2,3].map(i => <div key={i} className="h-64 bg-slate-200 animate-pulse"></div>)}
          </div>
        ) : (data.entries || []).length === 0 ? (
          <div className="bg-card border border-border rounded-lg p-12 text-center">
            <Trophy size={48} className="mx-auto mb-4 text-slate-300" />
            <p className="text-muted-foreground">No picker activity yet. Pickers will appear here once they start picking.</p>
          </div>
        ) : (
          <>
            {/* Top 3 Podium */}
            {top3.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                {top3.map((e) => (
                  <div
                    key={e.user_id}
                    className={`border-2 p-5 ${medalColors[e.medal] || 'bg-card border-border rounded-lg'}`}
                    data-testid={`podium-${e.rank}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Medal size={24} weight="fill" />
                        <span className="text-3xl font-bold">#{e.rank}</span>
                      </div>
                      <span className="text-4xl font-bold">
                        {e.score}
                      </span>
                    </div>
                    <p className="text-xl font-bold mb-1">{e.user_name}</p>
                    <p className="text-xs uppercase tracking-widest opacity-75 mb-4">{e.role} · score</p>

                    <div className="grid grid-cols-3 gap-2 text-center mb-3">
                      <div>
                        <p className="text-xs uppercase opacity-75">Picks</p>
                        <p className="text-2xl font-bold">{e.picks_period}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase opacity-75">Streak</p>
                        <p className="text-2xl font-bold flex items-center justify-center gap-1">
                          {e.streak_days}
                          {e.streak_days >= 3 && <Fire size={20} weight="fill" />}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs uppercase opacity-75">Speed</p>
                        <p className="text-2xl font-bold">
                          {e.avg_seconds_per_item !== null ? `${e.avg_seconds_per_item}s` : '—'}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-1">
                      {(e.badges || []).map(b => <BadgeChip key={b.code} badge={b} />)}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Rest of ranking table */}
            {rest.length > 0 && (
              <div className="bg-card border border-border rounded-lg overflow-x-auto">
                <table className="w-full table-dense">
                  <thead>
                    <tr>
                      <th className="w-16">Rank</th>
                      <th>Picker</th>
                      <th>Score</th>
                      <th>Picks</th>
                      <th>Streak</th>
                      <th>Avg Sec/Item</th>
                      <th>Total Picks</th>
                      <th>Badges</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rest.map(e => (
                      <tr key={e.user_id} data-testid={`leaderboard-row-${e.rank}`}>
                        <td className="font-mono font-bold text-lg">#{e.rank}</td>
                        <td>
                          <p className="font-bold">{e.user_name}</p>
                          <p className="text-xs text-muted-foreground uppercase">{e.role}</p>
                        </td>
                        <td className="font-mono font-bold text-brand">{e.score}</td>
                        <td className="font-mono">{e.picks_period}</td>
                        <td className="font-mono flex items-center gap-1">
                          {e.streak_days}
                          {e.streak_days >= 3 && <Fire size={14} weight="fill" className="text-orange-500" />}
                        </td>
                        <td className="font-mono">{e.avg_seconds_per_item !== null ? `${e.avg_seconds_per_item}s` : '—'}</td>
                        <td className="font-mono text-muted-foreground">{e.total_picks}</td>
                        <td>
                          <div className="flex flex-wrap gap-1">
                            {(e.badges || []).map(b => <BadgeChip key={b.code} badge={b} />)}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Scoring legend */}
            <div className="mt-6 p-4 bg-muted border border-border text-xs text-muted-foreground">
              <p className="font-bold uppercase tracking-widest mb-2">How score is calculated</p>
              <p><span className="font-mono font-bold">score = picks_period × 10 + streak_days × 50 + completed_lists × 5</span></p>
              <p className="mt-1">Badges: <span className="font-bold">Century Club</span> (100+ total picks), <span className="font-bold">Speed Demon</span> (≤30s/item), <span className="font-bold">Hot Week</span> (50+ picks this period), <span className="font-bold">Fire Streak</span> (3+ consecutive days).</p>
            </div>
          </>
        )}
        </>
        )}

        {/* Scan Accuracy — mis-scan analytics */}
        <section className="mt-10" data-testid="scan-accuracy-section">
          <div className="flex items-baseline justify-between flex-wrap gap-3 mb-3">
            <div>
              <h2 className="text-xl font-bold tracking-tight flex items-center gap-2">
                <Target size={22} weight="fill" className="text-emerald-600" /> Scan Accuracy
              </h2>
              <p className="text-xs text-muted-foreground">Wrong-SKU scans are logged per picker · useful for spotting retraining needs</p>
            </div>
            {!accuracyLoading && (
              <div className="flex items-center gap-3 text-right">
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Company accuracy</div>
                  <div className={`text-2xl font-bold tabular-nums ${accuracy.accuracy_pct >= 99 ? 'text-emerald-600' : accuracy.accuracy_pct >= 95 ? 'text-amber-600' : 'text-rose-600'}`} data-testid="company-accuracy-pct">
                    {accuracy.accuracy_pct}%
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Mis-scans</div>
                  <div className="text-2xl font-bold tabular-nums text-rose-600">{accuracy.total_mis_scans}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Total scans</div>
                  <div className="text-2xl font-bold tabular-nums">{accuracy.total_scans}</div>
                </div>
              </div>
            )}
          </div>
          {accuracyLoading ? (
            <div className="h-24 bg-slate-200 animate-pulse" />
          ) : (accuracy.rows || []).length === 0 ? (
            <div className="text-sm text-muted-foreground border border-dashed border-border p-4" data-testid="scan-accuracy-empty">
              No scans in the last {period} day{period !== 1 ? 's' : ''} — nothing to analyse yet.
            </div>
          ) : (
            <div className="overflow-x-auto border border-border">
              <table className="w-full text-sm" data-testid="scan-accuracy-table">
                <thead className="text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border bg-muted">
                  <tr>
                    <th className="text-left px-3 py-2">Picker</th>
                    <th className="text-right px-3 py-2">Total scans</th>
                    <th className="text-right px-3 py-2">Mis-scans</th>
                    <th className="text-right px-3 py-2">Accuracy</th>
                    <th className="text-left px-3 py-2">Flag</th>
                  </tr>
                </thead>
                <tbody>
                  {accuracy.rows.map((r) => {
                    const flag = r.accuracy_pct < 95 ? 'retraining' : r.accuracy_pct < 99 ? 'watch' : 'ok';
                    return (
                      <tr key={r.picker_id} className="border-b border-border" data-testid={`scan-accuracy-row-${r.picker_id}`}>
                        <td className="px-3 py-2">{r.picker_email || r.picker_id?.slice(0, 10)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{r.total_scans}</td>
                        <td className={`px-3 py-2 text-right tabular-nums font-bold ${r.mis_scans > 0 ? 'text-rose-600' : ''}`}>{r.mis_scans}</td>
                        <td className={`px-3 py-2 text-right tabular-nums font-mono font-bold ${r.accuracy_pct >= 99 ? 'text-emerald-600' : r.accuracy_pct >= 95 ? 'text-amber-600' : 'text-rose-600'}`}>
                          {r.accuracy_pct}%
                        </td>
                        <td className="px-3 py-2">
                          {flag === 'retraining' ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-rose-700 bg-rose-50 px-2 py-0.5">
                              <Warning size={10} weight="fill" /> Retraining recommended
                            </span>
                          ) : flag === 'watch' ? (
                            <span className="text-[10px] font-bold uppercase tracking-widest text-amber-700 bg-amber-50 px-2 py-0.5">
                              Watch
                            </span>
                          ) : (
                            <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 bg-emerald-50 px-2 py-0.5">
                              OK
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}

// =============================================================================
// ScanSpeedBoard — unified scan-speed leaderboard across Picking / Receiving /
// Production. Shows units scanned per user with a visual breakdown chip stack
// and the calculated units/hour. Highlights top 3 with medal colors.
// =============================================================================
function ScanSpeedBoard({ data, loading }) {
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">{[1,2,3].map(i => <div key={i} className="h-40 bg-slate-200 animate-pulse"></div>)}</div>;
  }
  const entries = data?.entries || [];
  if (entries.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-12 text-center" data-testid="scan-speed-empty">
        <Barcode size={48} className="mx-auto mb-4 text-slate-300" />
        <p className="text-muted-foreground">No scan activity in the selected period.</p>
        <p className="text-xs text-muted-foreground mt-2">
          Activity is tracked from Picking, Receiving, and Production Order scans.
        </p>
      </div>
    );
  }

  const sourceChips = [
    { key: 'pick', label: 'Pick', Icon: HandGrabbing, color: 'bg-blue-100 text-blue-800 border-blue-200' },
    { key: 'pack', label: 'Pack', Icon: Package, color: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
    { key: 'receive', label: 'Receive', Icon: Truck, color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
    { key: 'produce_consume', label: 'Consume', Icon: Factory, color: 'bg-amber-100 text-amber-800 border-amber-200' },
    { key: 'produce_yield', label: 'Yield', Icon: Factory, color: 'bg-rose-100 text-rose-800 border-rose-200' },
  ];

  const top3 = entries.slice(0, 3);
  const rest = entries.slice(3);
  const medalBg = ['bg-gradient-to-br from-yellow-400 to-yellow-600', 'bg-gradient-to-br from-slate-300 to-slate-500', 'bg-gradient-to-br from-amber-600 to-amber-800'];

  return (
    <div data-testid="scan-speed-board">
      {/* Podium */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {top3.map((e, idx) => (
          <div
            key={e.user_id}
            data-testid={`scan-podium-${idx + 1}`}
            className={`border-2 ${idx === 0 ? 'border-yellow-500' : idx === 1 ? 'border-slate-400' : 'border-amber-700'} bg-card p-5 rounded-lg relative`}
          >
            <div className={`absolute -top-3 -left-3 w-10 h-10 rounded-full ${medalBg[idx]} flex items-center justify-center shadow-lg`}>
              <Medal size={20} weight="fill" className="text-white" />
            </div>
            <div className="ml-9">
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Rank #{e.rank}</p>
              <h3 className="text-lg font-bold truncate" title={e.name}>{e.name}</h3>
              <div className="flex items-baseline gap-2 mt-2">
                <span className="text-3xl font-bold tabular-nums">{e.total_units.toLocaleString()}</span>
                <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">units</span>
              </div>
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-1">
                <Lightning size={13} weight="fill" className="text-amber-500" />
                <span className="tabular-nums font-mono">{e.units_per_hour}</span>
                <span className="text-xs">units/hr</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-1 mt-3">
              {sourceChips.map(({ key, label, Icon, color }) => {
                const n = e.breakdown?.[key] || 0;
                if (n === 0) return null;
                return (
                  <span key={key} className={`inline-flex items-center gap-1 px-1.5 py-0.5 border text-[10px] font-bold uppercase tracking-wider rounded ${color}`}>
                    <Icon size={10} weight="fill" /> {label} {n}
                  </span>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Rest of the ranking table */}
      {rest.length > 0 && (
        <div className="bg-card border border-border rounded-lg overflow-x-auto">
          <table className="w-full table-dense" data-testid="scan-speed-table">
            <thead>
              <tr>
                <th className="w-12">Rank</th>
                <th>Operator</th>
                <th className="text-right">Total units</th>
                <th className="text-right">Units/hr</th>
                <th>Breakdown</th>
              </tr>
            </thead>
            <tbody>
              {rest.map((e) => (
                <tr key={e.user_id} data-testid={`scan-row-${e.user_id}`}>
                  <td className="font-mono font-bold text-muted-foreground">#{e.rank}</td>
                  <td>
                    <div className="font-semibold truncate" title={e.name}>{e.name}</div>
                    <div className="text-xs text-muted-foreground uppercase tracking-wider">{e.role}</div>
                  </td>
                  <td className="text-right font-mono font-bold tabular-nums">{e.total_units.toLocaleString()}</td>
                  <td className="text-right font-mono tabular-nums">{e.units_per_hour}</td>
                  <td>
                    <div className="flex flex-wrap gap-1">
                      {sourceChips.map(({ key, label, Icon, color }) => {
                        const n = e.breakdown?.[key] || 0;
                        if (n === 0) return null;
                        return (
                          <span key={key} className={`inline-flex items-center gap-1 px-1.5 py-0.5 border text-[10px] font-bold uppercase tracking-wider rounded ${color}`}>
                            <Icon size={10} weight="fill" /> {label} {n}
                          </span>
                        );
                      })}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-6 p-4 bg-muted border border-border text-xs text-muted-foreground">
        <p className="font-bold uppercase tracking-widest mb-2">How units/hour is calculated</p>
        <p>First scan → last scan window per operator across the selected period, floor of 5 minutes. Covers multi-shift stacking naturally.</p>
        <p className="mt-1">Sources: <span className="font-bold">pick</span>, <span className="font-bold">pack</span>, <span className="font-bold">receive</span>, <span className="font-bold">consume</span> (raw materials into production), <span className="font-bold">yield</span> (finished goods out of production).</p>
      </div>
    </div>
  );
}
